import { useState } from "react";
import { Carousel } from "react-bootstrap";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import facebookIcon from "../../../assets/icons/facebook.svg";
import "./LiveSessionCard.css";

/**
 * LiveSessionCard
 *
 * Left panel  – static doctor/host card
 * Right panel – auto-scrolling carousel; on hover reveals date/time/location overlay
 *
 * Props:
 *  hostCard   { image, badge, platform, topic, doctorName, onRegister }
 *  sessions   [{ image, priority, title, date, time, location, onRegister }]
 */
const LiveSessionCard = ({ hostCard = {}, sessions = [] }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <div className="lsc-wrapper">
      {/* ── LEFT CARD ── */}
      <div className="lsc-left">
        {hostCard.badge && (
          <span className="lsc-crown" aria-label="featured">
            ♛
          </span>
        )}

        <div className="lsc-left__img-wrap">
          <img
            src={hostCard.image}
            alt={hostCard.doctorName || "Host"}
            className="lsc-left__img"
          />
          <div className="lsc-left__gradient" />
        </div>

        <div className="lsc-left__body">
          <div className="lsc-left__platform-row">
            {hostCard.platformIcon && (
              <img
                src={hostCard.platformIcon}
                alt="platform"
                className="lsc-platform-icon"
              />
            )}
            <span className="lsc-live-badge">LIVE</span>
          </div>

          <p className="lsc-left__join-text">
            JOIN OUR FACEBOOK LIVE SESSION ON
          </p>
          <h3 className="lsc-left__topic">{hostCard.topic}</h3>
          <p className="lsc-left__with">WITH</p>
          <h4 className="lsc-left__doctor">{hostCard.doctorName}</h4>

          <button
            className="lsc-register-btn"
            onClick={hostCard.onRegister}
          >
            Register Now
          </button>
        </div>
      </div>

      {/* ── RIGHT CAROUSEL ── */}
      <div className="lsc-right">
        <Carousel
          activeIndex={activeIndex}
          onSelect={(idx) => setActiveIndex(idx)}
          interval={3000}
          indicators={true}
          controls={true}
          prevIcon={<span className="lsc-carousel-arrow"><MdChevronLeft size={22} /></span>}
          nextIcon={<span className="lsc-carousel-arrow"><MdChevronRight size={22} /></span>}
        >
          {sessions.map((session, idx) => (
            <Carousel.Item key={idx}>
              <div
                className="lsc-slide"
                onMouseEnter={() => setHoveredIndex(idx)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <img
                  src={session.image}
                  alt={session.title}
                  className="lsc-slide__img"
                />

                {/* dark overlay always visible at top */}
                <div className="lsc-slide__top-overlay">
                  <p className="lsc-slide__priority">{session.priority}</p>
                  <h4 className="lsc-slide__title">{session.title}</h4>

                  {/* date/time/location – visible only on hover */}
                  <div
                    className={`lsc-slide__meta ${
                      hoveredIndex === idx ? "lsc-slide__meta--visible" : ""
                    }`}
                  >
                    <p className="lsc-slide__date">{session.date}</p>
                    <p className="lsc-slide__time">{session.time}</p>
                    <p className="lsc-slide__location">{session.location}</p>
                  </div>
                </div>

                <div className="lsc-slide__bottom">
                  {/* indicators gap — actual indicators rendered by Carousel, positioned via CSS */}
                  <div className="lsc-slide__indicators-gap" />
                  <button
                    className="lsc-register-btn lsc-register-btn--slide"
                    onClick={session.onRegister}
                  >
                    Register Now
                  </button>
                </div>
              </div>
            </Carousel.Item>
          ))}
        </Carousel>
      </div>
    </div>
  );
};

export default LiveSessionCard;
